import Vue from 'vue';
import {
  TM_API_ENDPOINTS,
  TM_MESSAGES,
  isDownloadInProgress,
  isDownloadFailed,
  DEFAULT_FILE_INGESTION_TIMEOUT_MINUTES,
} from '../utils/constants';
import { extractDrillDownFieldNames } from '../utils/tmReportDrilldownUtils';

const $axios = Vue.axios;

/**
 * Build pid (+ optional linkedtasks) for bot/analysis fcAccess extractors.
 * Prefer explicit overrides; fall back to stored analysisDetails.
 */
function botAccessFields(state = {}, overrides = {}) {
  const details = state.analysisDetails || {};
  const pid = overrides.pid || details.pid || '';
  const raw = overrides.linkedtasks != null ? overrides.linkedtasks : details.linkedtasks;
  const linkedtasks = (Array.isArray(raw) ? raw : [])
    .map((t) => (t && t._id) || t)
    .filter(Boolean);
  const fields = {};
  if (pid) fields.pid = pid;
  if (linkedtasks.length) fields.linkedtasks = linkedtasks;
  return fields;
}

/** Request body shape: top-level pid + nested data.pid (and linkedtasks when present). */
function botAccessBody(state, dataFields = {}, overrides = {}) {
  const access = botAccessFields(state, overrides);
  return {
    pid: access.pid || undefined,
    data: { ...dataFields, ...access },
  };
}

function parseFormDataResponse(responseData) {
  const payload = responseData?.data || {};
  const fileingestionTimeout = Number(payload.fileingestionTimeout);
  return {
    analysisTypeItems: payload.analysisTypeItems || [],
    fileTypeItems: payload.fileTypeItems || [],
    results: payload.results || [],
    hideManualTagTabs: Array.isArray(payload.hideManualTagTabs) ? payload.hideManualTagTabs : [],
    // Unique field names only — reportTable on config is metadata, not used for eligibility
    drillDownFields: extractDrillDownFieldNames(payload.drillDownFields),
    fileingestionTimeout: Number.isFinite(fileingestionTimeout) && fileingestionTimeout > 0
      ? fileingestionTimeout
      : DEFAULT_FILE_INGESTION_TIMEOUT_MINUTES,
  };
}

/** Normalize rootState.client (id string or client object) to a stable id string. */
function resolveClientId(client) {
  return (client && typeof client === 'object' ? client._id : client) ?? null;
}

export default {
  namespaced: true,
  state: {
    loading: false,
    analysisDetails: {},
    analysisLoading: false,
    downloadHistory: [],
    loadingExcelHistory: false,
    middlewareValues: [],
    middlewareLoading: false,
    filterList: [],
    filterLoading: false,
    botDetails: {},
    botId: '',
    metadata: {},
    formData: null,
    formDataClient: null,
    reportDrillDown: {
      active: null,
      snapshot: null,
    },
    analysisList: [],
    /** Last applied filter tab per collection: 'saved' | 'playground' */
    lastActiveFilterTabByCollection: {},
  },

  getters: {
    isLoading: state => state.loading,
    analysisDetails: state => state.analysisDetails,
    isAnalysisLoading: state => state.analysisLoading,
    downloadHistory: state => state.downloadHistory,
    isLoadingExcelHistory: state => state.loadingExcelHistory,
    middlewareValues: state => state.middlewareValues,
    isMiddlewareLoading: state => state.middlewareLoading,
    filterList: state => state.filterList,
    isFilterLoading: state => state.filterLoading,
    botDetails: state => state.botDetails,
    botId: state => state.botId,
    metadata: state => state.metadata,
    formData: (state) => state.formData || {
      analysisTypeItems: [],
      fileTypeItems: [],
      results: [],
      hideManualTagTabs: [],
      drillDownFields: [],
      fileingestionTimeout: DEFAULT_FILE_INGESTION_TIMEOUT_MINUTES,
    },
    fileIngestionTimeoutMs: (_state, getters) => {
      const minutes = getters.formData.fileingestionTimeout ?? DEFAULT_FILE_INGESTION_TIMEOUT_MINUTES;
      return minutes * 60 * 1000;
    },
    isFormDataLoaded: (state) => state.formData != null,
    reportDrilldownActive: state => state.reportDrillDown.active,
    reportSnapshot: state => state.reportDrillDown.snapshot,
    analysisList: state => state.analysisList,
    lastActiveFilterTab: state => collection => {
      if (!collection) return null;
      return state.lastActiveFilterTabByCollection[collection] ?? null;
    },
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ANALYSIS_DETAILS(state, details) {
      state.analysisDetails = details;
    },
    SET_ANALYSIS_LOADING(state, loading) {
      state.analysisLoading = loading;
    },
    SET_DOWNLOAD_HISTORY(state, history) {
      state.downloadHistory = history;
    },
    SET_LOADING_EXCEL_HISTORY(state, loading) {
      state.loadingExcelHistory = loading;
    },
    SET_MIDDLEWARE_VALUES(state, values) {
      state.middlewareValues = values;
    },
    SET_MIDDLEWARE_LOADING(state, loading) {
      state.middlewareLoading = loading;
    },
    SET_FILTER_LIST(state, filters) {
      state.filterList = filters;
    },
    SET_FILTER_LOADING(state, loading) {
      state.filterLoading = loading;
    },
    SET_BOT_DETAILS(state, details) {
      state.botDetails = details;
    },
    SET_BOT_ID(state, id) {
      state.botId = id;
    },
    SET_METADATA(state, metadata) {
      state.metadata = metadata;
    },
    SET_FORM_DATA(state, { data, client }) {
      state.formData = data;
      state.formDataClient = resolveClientId(client);
    },
    SET_REPORT_DRILLDOWN_ACTIVE(state, active) {
      state.reportDrillDown.active = active;
    },
    SAVE_REPORT_SNAPSHOT(state, snapshot) {
      state.reportDrillDown.snapshot = snapshot
        ? { ...snapshot, savedAt: Date.now() }
        : null;
    },
    CLEAR_REPORT_DRILLDOWN(state) {
      state.reportDrillDown.active = null;
      state.reportDrillDown.snapshot = null;
    },
    SET_ANALYSIS_LIST(state, list) {
      state.analysisList = list;
    },
    SET_LAST_ACTIVE_FILTER_TAB(state, { collection, tab }) {
      if (!collection) return;
      if (tab === null || tab === undefined) {
        Vue.delete(state.lastActiveFilterTabByCollection, collection);
      } else {
        Vue.set(state.lastActiveFilterTabByCollection, collection, tab);
      }
    },
  },

  actions: {
    async fetchAnalysisDetails({ commit }, { id, anaid }) {
      if (!id || !anaid) {
        console.error('[TransactionMatchingStore]: fetchAnalysisDetails - missing id or anaid');
        commit('SET_ANALYSIS_DETAILS', {});
        return {};
      }

      commit('SET_ANALYSIS_LOADING', true);
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_TM_ANALYSIS_DETAILS}/${id}/${anaid}`);
        const resData = response?.data || {};
        const payload = resData?.data;

        let analysisDetails = {};
        if (resData.status === 'success') {
          if (Array.isArray(payload)) {
            analysisDetails = payload[0] || {};
          } else if (payload && typeof payload === 'object') {
            // Some endpoints may wrap the array/object under payload.data / payload.analysisDetails
            if (Array.isArray(payload.data)) {
              analysisDetails = payload.data[0] || {};
            } else if (payload.analysisDetails && typeof payload.analysisDetails === 'object') {
              analysisDetails = payload.analysisDetails;
            } else {
              analysisDetails = payload;
            }
          }
        }

        // TM APIs return this as `actiondetails`; normalize to `analysisDetails.actiondetails`
        // (keep minimal fallback for cases where it comes alongside the payload object)
        const payloadActionDetails =
          (!Array.isArray(payload) && payload && typeof payload === 'object') ? payload.actiondetails : undefined;

        const actiondetails = analysisDetails.actiondetails ?? resData.actiondetails ?? payloadActionDetails;

        if (analysisDetails.actiondetails == null && actiondetails != null) {
          analysisDetails = { ...analysisDetails, actiondetails };
        }

        commit('SET_ANALYSIS_DETAILS', analysisDetails);
        return analysisDetails;
      } catch (err) {
        commit('SET_ANALYSIS_DETAILS', {});
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: fetchAnalysisDetails error', err);
        return {};
      } finally {
        commit('SET_ANALYSIS_LOADING', false);
      }
    },

    async generateResult({
      commit,
      dispatch,
    }, {
      item,
      botId,
      tables,
      filters,
      sort,
      dataFilters,
      collection,
      version,
      field,
      runId,
      reportDate,
      reportTable,
      dateScope,
    }) {
      commit('SET_LOADING', true);
      const payloadEntry = { section: item };
      if (tables && tables.length > 0) {
        payloadEntry.tables = tables;
      }
      if (filters?.groups?.length > 0) {
        payloadEntry.filters = filters;
      }
      if (sort?.length > 0) {
        payloadEntry.sort = sort;
      }
      if (dataFilters != null && dataFilters !== '') {
        const hasFilters = typeof dataFilters === 'string'
          ? dataFilters.length > 0
          : Array.isArray(dataFilters) && dataFilters.length > 0;
        if (hasFilters) {
          payloadEntry.dataFilters = dataFilters;
        }
      }
      if (collection) {
        payloadEntry.collection = collection;
      }
      if (version) {
        payloadEntry.version = version;
      }
      if (field) {
        payloadEntry.field = field;
      }
      if (runId) {
        payloadEntry.runId = runId;
      }
      if (reportDate) {
        payloadEntry.reportDate = reportDate;
      }
      if (reportTable) {
        payloadEntry.reportTable = reportTable;
      }
      if (dateScope) {
        payloadEntry.dateScope = dateScope;
      }
      const dataPayload = [payloadEntry];

      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GENERATE_RESULT}/${botId}`, { data: dataPayload });

        if (response.data.status === "success") {
          const resultData = response.data.data[0] || {};
          
          // Trigger download check immediately as per original logic
          await dispatch('downloadResult', { item, botId });

          if (isDownloadInProgress(resultData.stage)) {
            commit('sbSuccess', TM_MESSAGES.FILE_GENERATING, { root: true });
          } else if (isDownloadFailed(resultData.stage)) {
            commit('sbError', TM_MESSAGES.ERROR_GENERATING, { root: true });
          } else if (resultData.upload) {
            // Return the upload path so component can handle the actual download
            // or we could use Vue.prototype.$nova if available in store context via helper
            if (Vue.prototype.$nova) {
              Vue.prototype.$nova.downloadFile(resultData.upload);
            }
            return resultData.upload;
          } else {
             // "no file found to download" - as in original code, maybe return null
             return null;
          }
        } else {
          throw new Error(response.data.message || TM_MESSAGES.UNKNOWN_ERROR);
        }
      } catch (err) {
        commit('sbError', err.message || err || TM_MESSAGES.UNKNOWN_ERROR, { root: true });
        console.error('[TransactionMatchingStore]: downloadResult error', err);
        throw err; // Propagate error if component needs to know
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async downloadResult({ commit }, { item, botId }) {
      commit('SET_LOADING', true);
      const dataPayload = [{ section: item }];

      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_RECENT_DOWNLOADS}/${botId}`, { data: dataPayload });

        if (response.data.status === "success") {
          const resultData = response.data.data[0] || {};

          if (isDownloadInProgress(resultData.stage)) {
             commit('sbSuccess', TM_MESSAGES.FILE_GENERATING, { root: true });
          } else if (isDownloadFailed(resultData.stage)) {
             commit('sbError', TM_MESSAGES.ERROR_GENERATING, { root: true });
          } else if (resultData.upload) {
            if (Vue.prototype.$nova) {
              Vue.prototype.$nova.downloadFile(resultData.upload);
            }
            return resultData.upload;
          } else {
             return null;
          }
        } else {
          throw new Error(response.data.message || TM_MESSAGES.UNKNOWN_ERROR);
        }
      } catch (err) {
        commit('sbError', err.message || err || TM_MESSAGES.UNKNOWN_ERROR, { root: true });
        console.error(err);
        throw err;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Bulk manual match for two-file transaction matching.
     * Uploads files and sends them for consolidated manual matching.
     * @param {Object} payload - { uploadData, tmaId, runId, clientCode }
     */
    async bulkManualMatchTwoFile({ commit }, { uploadData, tmaId, runid, clientCode }) {
      try {
        const data = {
          upload: uploadData,
          tmaid: tmaId,
          runid: runid,
          clientCode: clientCode?.toString() || '',
        };
        const response = await $axios.post(TM_API_ENDPOINTS.TWO_FILES_BULK_MANUAL_MATCH, data);
        if (response.data.status === 'success') {
          commit('sbSuccess', TM_MESSAGES.BULK_MANUAL_MATCH_SUCCESS, { root: true });
          return response.data;
        } else {
          throw new Error(response.data.message || TM_MESSAGES.UNKNOWN_ERROR);
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: bulkManualMatchTwoFile error', err);
        commit('sbError', err.message || err || TM_MESSAGES.UNKNOWN_ERROR, { root: true });
        throw err;
      } 
    },

    async getMiddlewareList({ commit }, { pid, tmaid, bpid }) {
      if (!pid || !tmaid || !bpid) {
        console.error('[TransactionMatchingStore]: getMiddlewareList — missing params');
        return [];
      }
      commit('SET_MIDDLEWARE_LOADING', true);
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_TM_MIDDLEWARE}/${pid}/${tmaid}/${bpid}`);
        if (response.data.status === 'success' && response.data.data.length > 0) {
          commit('SET_MIDDLEWARE_VALUES', response.data.data);
          return response.data.data;
        } else {
          commit('SET_MIDDLEWARE_VALUES', []);
          return [];
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: getMiddlewareList error', err);
        return [];
      } finally {
        commit('SET_MIDDLEWARE_LOADING', false);
      }
    },

    async addMiddleware({ commit, state }, { middlewareData, pid, linkedtasks }) {
      try {
        const body = botAccessBody(state, middlewareData || {}, { pid, linkedtasks });
        const response = await $axios.post(TM_API_ENDPOINTS.ADD_TM_MIDDLEWARE, body);
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Mapping master successfully saved', { root: true });
          return { status: 'success' };
        } else {
          if (response.data.data?.length > 0 && response.data.data[0].index0) {
            return { status: 'validation_error', errors: response.data.data[0].index0 };
          }
          throw new Error(response.data.message || 'Failed to add middleware');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: addMiddleware error', err);
        throw err;
      }
    },

    async updateMiddleware({ commit, state }, { id, middlewareData, pid, linkedtasks }) {
      try {
        const body = botAccessBody(state, middlewareData || {}, { pid, linkedtasks });
        const response = await $axios.post(`${TM_API_ENDPOINTS.UPDATE_TM_MIDDLEWARE}/${id}`, body);
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Mapping master successfully saved', { root: true });
          return { status: 'success' };
        } else {
          if (response.data.data?.length > 0 && response.data.data[0].index0) {
            return { status: 'validation_error', errors: response.data.data[0].index0 };
          }
          throw new Error(response.data.message || 'Failed to update middleware');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: updateMiddleware error', err);
        throw err;
      }
    },

    async deleteMiddleware({ commit, state }, { id, pid, linkedtasks }) {
      try {
        const body = botAccessBody(state, {}, { pid, linkedtasks });
        const response = await $axios.post(`${TM_API_ENDPOINTS.DELETE_TM_MIDDLEWARE}/${id}`, body);
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Mapping master successfully deleted', { root: true });
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || 'Failed to delete middleware');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: deleteMiddleware error', err);
        throw err;
      }
    },

    async enableMiddleware({ commit, state }, { id, action, pid, linkedtasks }) {
      const endpoint = action === 'Enable'
        ? TM_API_ENDPOINTS.ENABLE_TM_MIDDLEWARE
        : TM_API_ENDPOINTS.DISABLE_TM_MIDDLEWARE;
      try {
        const body = botAccessBody(state, {}, { pid, linkedtasks });
        const response = await $axios.post(`${endpoint}/${id}`, body);
        if (response.data.status === 'success') {
          commit('sbSuccess', `Mapping master successfully ${action.toLowerCase()}d`, { root: true });
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || 'Error performing action');
        }
      } catch (err) {
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        console.error('[TransactionMatchingStore]: enableMiddleware error', err);
        throw err;
      }
    },

    /**
     * Fetch filter list via tmfilter/get API.
     * Also triggers backend key generation for legacy data without keys.
     * @param {Object} payload - { bpid }
     * @returns {Array} Filter list
     */
    async getFilterList({ commit }, { bpid }) {
      if (!bpid) {
        console.error('[TransactionMatchingStore]: getFilterList — missing bpid');
        return [];
      }
      commit('SET_FILTER_LOADING', true);
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_TM_FILTER}/${bpid}`);
        if (response.data.status === 'success' && response.data.data.length > 0) {
          commit('SET_FILTER_LIST', response.data.data);
          return response.data.data;
        } else {
          commit('SET_FILTER_LIST', []);
          return [];
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: getFilterList error', err);
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        return [];
      } finally {
        commit('SET_FILTER_LOADING', false);
      }
    },

    /**
     * Add or update a filter via upsert API.
     * @param {Object} payload - { bpid, filterData }
     */
    async upsertFilter({ commit, state }, { bpid, filterData, pid, linkedtasks }) {
      if (!bpid) {
        console.error('[TransactionMatchingStore]: upsertFilter — missing bpid');
        throw new Error('Missing bpid');
      }
      try {
        const body = botAccessBody(state, { filter: filterData }, { pid, linkedtasks });
        const response = await $axios.post(`${TM_API_ENDPOINTS.UPSERT_TM_FILTER}/${bpid}`, body);
        if (response.data.status === 'success') {
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || 'Failed to upsert filter');
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: upsertFilter error', err);
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Delete a filter via delete API.
     * @param {Object} payload - { bpid, key }
     */
    async deleteFilter({ commit, state }, { bpid, key, pid, linkedtasks }) {
      if (!bpid || !key) {
        console.error('[TransactionMatchingStore]: deleteFilter — missing bpid or key');
        throw new Error('Missing bpid or key');
      }
      try {
        const body = botAccessBody(state, {}, { pid, linkedtasks });
        const response = await $axios.delete(`${TM_API_ENDPOINTS.DELETE_TM_FILTER}/${bpid}/${key}`, { data: body });
        if (response.data.status === 'success') {
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || 'Failed to delete filter');
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: deleteFilter error', err);
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Toggle filter enabled/disabled status.
     * @param {Object} payload - { bpid, key, enable }
     */
    async toggleFilterStatus({ commit, state }, { bpid, key, enable, pid, linkedtasks }) {
      if (!bpid || !key) {
        console.error('[TransactionMatchingStore]: toggleFilterStatus — missing bpid or key');
        throw new Error('Missing bpid or key');
      }
      const endpoint = enable ? TM_API_ENDPOINTS.ENABLE_TM_FILTER : TM_API_ENDPOINTS.DISABLE_TM_FILTER;
      try {
        const body = botAccessBody(state, { filter: { key } }, { pid, linkedtasks });
        const response = await $axios.put(`${endpoint}/${bpid}`, body);
        if (response.data.status === 'success') {
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || `Failed to ${enable ? 'enable' : 'disable'} filter`);
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: toggleFilterStatus error', err);
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Reorder filters via reorder API.
     * @param {Object} payload - { bpid, orderedKeys } where orderedKeys is [{ key }, ...]
     */
    async reorderFilters({ commit, state }, { bpid, orderedKeys, pid, linkedtasks }) {
      if (!bpid) {
        console.error('[TransactionMatchingStore]: reorderFilters — missing bpid');
        throw new Error('Missing bpid');
      }
      try {
        const body = botAccessBody(state, { filters: orderedKeys }, { pid, linkedtasks });
        const response = await $axios.put(`${TM_API_ENDPOINTS.REORDER_TM_FILTER}/${bpid}`, body);
        if (response.data.status === 'success') {
          return { status: 'success' };
        } else {
          throw new Error(response.data.message || 'Failed to save filter order');
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: reorderFilters error', err);
        commit('sbError', err.message || err || 'Unknown error!', { root: true });
        throw err;
      }
    },

    async fetchDownloadHistory({ commit }, botId) {
      commit('SET_LOADING_EXCEL_HISTORY', true);
      // Removed "data: ..." payload as original code sent empty object {} in axios post body for this call
      // Wait, original code: this.axios.post("...", {})
      // My original store code: data: [{ section: 'Excel history' }] - Maybe this was for generate/downloadResult
      // But fetchDownloadHistory used empty object in component: this.axios.post(..., {})
      
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_RECENT_DOWNLOADS}/${botId}`, {}); // Empty body
        if (response.data.status === 'success') {
             let files = response.data.data || [];
             // Sort by created_at descending and take latest 5
             files = files
               .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
               .slice(0, 5);
             commit('SET_DOWNLOAD_HISTORY', files);
        } else {
             commit('SET_DOWNLOAD_HISTORY', []);
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchDownloadHistory error', err);
        commit('SET_DOWNLOAD_HISTORY', []);
      } finally {
        commit('SET_LOADING_EXCEL_HISTORY', false);
      }
    },

    /**
     * Fetch bot details by project id
     */
    async fetchBotDetails({ commit }, { id }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_BOT_DETAILS}/${id}`);
        if (response.data.status === 'success' && response.data.data.length > 0) {
          const data = response.data.data[0] || {};
          commit('SET_METADATA', data.metadata || {});
          return data;
        }
        return {};
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchBotDetails error', err);
        commit('sbError', err?.response?.data?.message || err?.message || err || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Load bot details with pid and bid
     */
    async loadBotDetails({ commit }, { pid, bid }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.LOAD_BOT_DETAILS}/${pid}/b/${bid}`);
        if (response.data.status === 'success' && response.data.data.length > 0) {
          commit('SET_BOT_DETAILS', response.data.data[0] || {});
          return response.data.data[0] || {};
        }
        return {};
      } catch (err) {
        console.error('[TransactionMatchingStore]: loadBotDetails error', err);
        commit('sbError', err.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Get bot id by name
     */
    async fetchBotIdByName({ commit }, { names }) {
      try {
        const response = await $axios.post(TM_API_ENDPOINTS.GET_BOT_ID_BY_NAME, { data: names });
        if (response.data.status === 'success') {
          const id = response.data.data[0]?._id || '';
          commit('SET_BOT_ID', id);
          return id;
        }
        return '';
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchBotIdByName error', err);
        throw err;
      }
    },

    /**
     * Fetch analysis form data (types, file types, results, drill-down fields).
     * Client is injected by the axios request interceptor — do not pass it here.
     * Uses v3 (tm/form-data); falls back to v2 (tm/getformdata) when v3 is unavailable.
     * Cached per client until forceRefresh is true.
     */
    async fetchFormData({ commit, state, rootState }, { forceRefresh = false } = {}) {
      const clientId = resolveClientId(rootState.client || rootState.user?.client);
      if (!forceRefresh && state.formData && state.formDataClient === clientId) {
        return state.formData;
      }

      const endpoints = [
        TM_API_ENDPOINTS.GET_FORM_DATA,
        TM_API_ENDPOINTS.GET_FORM_DATA_V2,
      ];

      let lastError = null;

      for (const url of endpoints) {
        try {
          const response = await $axios.post(url);
          if (response.data.status === 'success') {
            const data = parseFormDataResponse(response.data);
            commit('SET_FORM_DATA', { data, client: clientId });
            return data;
          }
          lastError = new Error(response.data.message || 'Failed to fetch form data');
        } catch (err) {
          lastError = err;
          console.warn('[TransactionMatchingStore]: fetchFormData failed for', url, err?.message || err);
        }
      }

      console.error('[TransactionMatchingStore]: fetchFormData error', lastError);
      commit(
        'sbError',
        lastError?.response?.data?.message || lastError?.message || 'Failed to load analysis form data',
        { root: true }
      );
      throw lastError;
    },

    /**
     * Fetch report drill-down annexure records for a report line field.
     * Called from TMResultsReportTab only — child views receive data via props.
     * @param {Object} payload
     * @param {string} payload.client
     * @param {string} payload.version
     * @param {string} payload.runId
     * @param {string} payload.reportDate
     * @param {string} payload.field
     * @param {'bbm'|'cbs'} [payload.reportTable] - required for shared labels (Nil Debit, NEFT Return)
     * @param {Object} [payload.filters]
     * @param {Object} [payload.pagination]
     * @param {Array} [payload.sort]
     * @param {boolean} [payload.forceRefresh]
     */
    async fetchReportDrilldown(_ctx, payload) {
      try {
        const response = await $axios.post(TM_API_ENDPOINTS.TM_REPORT_DRILLDOWN, payload);
        if (response.data.status === 'success') {
          return response.data.data;
        }
        throw new Error(response.data.message || 'Failed to fetch report drill-down data');
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchReportDrilldown error', err);
        throw err;
      }
    },

    /**
     * Fetch or trigger pending payouts report
     * @param {Object} payload - { bpid, reportDate, page?, limit? }
     * @returns {Promise<Object>} - { reportStatus, records?, totalCount?, page?, limit?, totalPages? }
     */
    async fetchPendingPayoutsReport(_ctx, {
      collection,
      reportDate,
      page = 1,
      limit = 50,
      client,
      tmaid,
      bpid,
      forceRefresh = false,
      filters = null,
      sort = null,
    }) {
      try {
        const body = {
          reportDate,
          pagination: { page, limit },
          client,
          tmaid,
          bpid,
          forceRefresh,
        };
        if (filters && filters.groups?.length) {
          body.filters = filters;
        }
        if (Array.isArray(sort) && sort.length > 0) {
          body.sort = sort;
        }
        const response = await $axios.post(
          `${TM_API_ENDPOINTS.GET_PENDING_PAYOUTS}/${encodeURIComponent(collection)}`,
          body
        );
        if (response.data.status === 'success') {
          return response.data.data;
        }
        throw new Error(response.data.message || 'Failed to fetch pending payouts report');
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchPendingPayoutsReport error', err);
        throw err;
      }
    },

    /**
     * Fetch annexure data (records, headers, pagination, defaults).
     * @param {Object} payload - { collection, ...requestPayload }
     * @returns {Promise<Object>} - Annexure response data
     */
    async fetchAnnexureData(_ctx, { collection, ...requestPayload }) {
      if (!collection) {
        throw new Error('Missing collection');
      }
      try {
        const response = await $axios.post(
          `${TM_API_ENDPOINTS.ANNEXURE}/${encodeURIComponent(collection)}`,
          requestPayload
        );
        if (response.data.status === 'success') {
          return response.data.data || {};
        }
        throw new Error(response.data.message || 'Failed to fetch annexure data');
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchAnnexureData error', err);
        throw err;
      }
    },

    /**
     * Fetch available report dates for a Report annexure collection.
     * `pid` is the financial-close cycle id used by route access checks.
     * `projectId` is the bot project id used to validate the annexure run list.
     * @param {Object} payload - { collection, projectId, verificationId, pid? }
     * @returns {Promise<string[]>} - Distinct report dates (YYYY-MM-DD), newest first
     */
    async fetchAnnexureReportDates({ state }, { collection, projectId, verificationId, pid }) {
      try {
        const access = botAccessFields(state, { pid });
        const response = await $axios.post(
          `${TM_API_ENDPOINTS.ANNEXURE_REPORT_DATES}/${encodeURIComponent(collection)}`,
          {
            ...(access.pid ? { pid: access.pid } : {}),
            ...(access.linkedtasks ? { linkedtasks: access.linkedtasks } : {}),
            projectId,
            verificationId,
          }
        );
        if (response.data.status === 'success') {
          return response.data.data?.dates || [];
        }
        throw new Error(response.data.message || 'Failed to fetch available report dates');
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchAnnexureReportDates error', err);
        throw err;
      }
    },

    /**
     * Fetch all TM analyses for a project
     */
    async fetchAnalysisList({ commit }, { pid }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_TM_ANALYSIS_DETAILS}/${pid}`);
        if (response.data.status === 'success') {
          commit('SET_ANALYSIS_LIST', response.data.data || []);
          return response.data.data || [];
        }
        return [];
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchAnalysisList error', err);
        throw err;
      }
    },

    /**
     * Add a new TM analysis
     */
    async addAnalysis({ commit }, { data }) {
      try {
        const pid = data?.pid || data?.cpid || data?.project || undefined;
        const response = await $axios.post(TM_API_ENDPOINTS.ADD_TM_ANALYSIS, { pid, data });
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Transaction Matching Added Successfully', { root: true });
          return { status: 'success' };
        } else {
          if (response.data.data?.length > 0 && response.data.data[0].index0) {
            return { status: 'validation_error', errors: response.data.data[0].index0 };
          }
          throw new Error(response.data.message || 'Error Adding Transaction Matching');
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: addAnalysis error', err);
        commit('sbError', err.response?.data?.message || err?.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Update an existing TM analysis
     */
    async updateAnalysis({ commit }, { id, data }) {
      try {
        const pid = data?.pid || data?.cpid || data?.project || undefined;
        const response = await $axios.post(`${TM_API_ENDPOINTS.UPDATE_TM_ANALYSIS}/${id}`, { pid, data });
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Transaction Matching Updated Successfully', { root: true });
          return { status: 'success' };
        }
        throw new Error(response.data.message || 'Error Updating Transaction Matching');
      } catch (err) {
        console.error('[TransactionMatchingStore]: updateAnalysis error', err);
        commit('sbError', err.response?.data?.message || err?.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Import a TM analysis from another cycle
     */
    async importAnalysis({ commit }, { data }) {
      try {
        // Import extractor: data.cpid || data.pid only (top-level cpid is not read)
        const pid = data?.cpid || data?.pid || undefined;
        const response = await $axios.post(TM_API_ENDPOINTS.IMPORT_TM_ANALYSIS, { pid, data });
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Analysis Imported Successfully', { root: true });
          return { status: 'success' };
        } else {
          if (response.data.data?.length > 0 && response.data.data[0].index0) {
            return { status: 'validation_error', errors: response.data.data[0].index0 };
          }
          throw new Error(response.data.message || 'Error Adding Import Analysis');
        }
      } catch (err) {
        console.error('[TransactionMatchingStore]: importAnalysis error', err);
        commit('sbError', err.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Resync an imported TM analysis from its source
     */
    async resyncAnalysis({ commit, state }, { id, pid }) {
      try {
        const body = botAccessBody(state, {}, { pid });
        const response = await $axios.post(`${TM_API_ENDPOINTS.RESYNC_TM_ANALYSIS}/${id}`, body);
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Resync initiated successfully', { root: true });
          return { status: 'success' };
        }
        throw new Error(response.data.message || 'Error initiating resync');
      } catch (err) {
        console.error('[TransactionMatchingStore]: resyncAnalysis error', err);
        commit('sbError', err.response?.data?.message || err?.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Load TM analysis config (loadTransactionMatchingAnalysis)
     */
    async loadAnalysisConfig({ commit }, { tmaid, pid, bid }) {
      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.LOAD_TM_ANALYSIS}/${tmaid}`, {
          data: { pid, bid },
        });
        if (response.data.status === 'success' && response.data.data.length > 0) {
          return response.data.data[0] || {};
        }
        return {};
      } catch (err) {
        console.error('[TransactionMatchingStore]: loadAnalysisConfig error', err);
        commit('sbError', err.message || 'Unknown error!', { root: true });
        throw err;
      }
    },

    /**
     * Resolve upload display names by file id(s).
     * @param {object} _context
     * @param {{ fileIds?: string|string[] }} payload
     * @returns {Promise<Record<string, string>>} Map of upload id → display name
     */
    async fetchUploadFilenames(_context, { fileIds } = {}) {
      const ids = (Array.isArray(fileIds) ? fileIds : [fileIds])
        .map((id) => String(id || '').trim())
        .filter(Boolean);
      const uniqueIds = [...new Set(ids)];
      if (!uniqueIds.length) return {};

      try {
        const response = await $axios.post(`${TM_API_ENDPOINTS.GET_UPLOAD_FILENAMES}/${uniqueIds.join(',')}`);
        const nameById = {};
        (response?.data?.data || []).forEach((upload) => {
          nameById[String(upload._id)] = upload.uploadname || upload.originalname || upload.modifiedname || '';
        });
        return nameById;
      } catch (err) {
        console.error('[TransactionMatchingStore]: fetchUploadFilenames error', err);
        return {};
      }
    },
  }
};
